import { Box, Container } from "@mui/material";
import Banner from "components/templates/Banner";
import DynamicsList from "./DynamicsList";
import CrossCountryPrice from "./CrossCountryPrice";
import Tanfeethi from "./Tanfeethi";
import AlMasar from "./AlMasar";
import { DynamicLookupList, LOOKUPS, lookupSlug, lookupIndexBySlug } from "./Selections";
import KioskHomeVideo from "./KioskHomeVideo/KioskHomeVideo";
import ContentfulCache from "./ContentfulCache";
import AggregatorMarkup from "./AggregatorMarkup";
import DynamicsTabBar from "./DynamicsTabBar";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const tabLabels = [
    "Bookings Time",
    // "App Banner Pic",
    // "Jeeny Banner",
    "Cross Country prices",
    "Tanfeethi",
    "Al Masar",
    "Selections",
    "Kiosk Home Video",
    "Contentful Cache",
    "Aggregator Markup",
];

const slugify = (label: string) => label.toLowerCase().replace(/\s+/g, "-");

const SELECTIONS_SLUG = slugify("Selections");
const SELECTIONS_TAB = tabLabels.indexOf("Selections");
// Where collapsing lands. Leaving Selections active with its children hidden
// left the page pointing at a section the user had just navigated out of.
const DEFAULT_TAB = 0;

// Movement Type, Platform Type and the three sub-status lists used to be
// top-level tabs. Their old slugs still resolve, to the Selections tab with the
// matching list open, so existing links and bookmarks keep working.
const initialTab = (tabParam: string | null) => {
    const idx = tabLabels.findIndex((l) => slugify(l) === tabParam);
    if (idx >= 0) return idx;
    if (lookupIndexBySlug(tabParam) >= 0) return SELECTIONS_TAB;
    return 0;
};

const initialList = (params: URLSearchParams) => {
    const fromList = lookupIndexBySlug(params.get("list"));
    if (fromList >= 0) return fromList;
    const fromLegacyTab = lookupIndexBySlug(params.get("tab"));
    return fromLegacyTab >= 0 ? fromLegacyTab : 0;
};

export default function City() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(() => initialTab(searchParams.get("tab")));
    const [list, setList] = useState(() => initialList(searchParams));
    // Landing on Selections (including via a legacy slug) opens the lists
    // straight away — that is the only reason to be on that tab.
    const [expanded, setExpanded] = useState(
        () => initialTab(searchParams.get("tab")) === SELECTIONS_TAB
    );

    // Single writer for the query string, so the two levels can't race.
    // Refreshing preserves both.
    useEffect(() => {
        const label = tabLabels[tab];
        if (!label) return;
        const slug = slugify(label);
        const next: Record<string, string> = { tab: slug };
        if (slug === SELECTIONS_SLUG) next.list = lookupSlug(LOOKUPS[list].label);
        setSearchParams(next, { replace: true });
    }, [tab, list, setSearchParams]);

    const onExpand = () => {
        setTab(SELECTIONS_TAB);
        setExpanded(true);
    };

    const onCollapse = () => {
        setExpanded(false);
        setTab(DEFAULT_TAB);
    };

    const selectedLookup = LOOKUPS[list] ?? LOOKUPS[0];

    const parentElements = [
        <DynamicsList />,
        // <BannerPic />,
        // <BannerJeeny />,
        <CrossCountryPrice />,
        <Tanfeethi />,
        <AlMasar />,
        null, // Selections renders the active lookup list instead
        <KioskHomeVideo />,
        <ContentfulCache />,
        <AggregatorMarkup />,
    ];

    return (
        <div>
            <Container maxWidth="lg">
                <Banner heading="Dynamics"></Banner>
                <DynamicsTabBar
                    tab={tab}
                    child={list}
                    expanded={expanded}
                    parentLabels={tabLabels}
                    childLabels={LOOKUPS.map((l) => l.label)}
                    expandableIndex={SELECTIONS_TAB}
                    onTabChange={setTab}
                    onChildChange={setList}
                    onExpand={onExpand}
                    onCollapse={onCollapse}
                />
                <Box
                    // Keyed on what is actually shown, so switching section or
                    // list replays the fade. Same easing as the tab bar so the
                    // two read as one movement.
                    key={tab === SELECTIONS_TAB ? selectedLookup.type : `tab-${tab}`}
                    sx={{
                        pt: 2,
                        pb: 2,
                        animation: "dynamicsPanelIn .22s ease-out",
                        "@keyframes dynamicsPanelIn": {
                            from: { opacity: 0, transform: "translateY(4px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                    }}
                >
                    {tab === SELECTIONS_TAB ? (
                        // The wrapper's key already remounts this on list change,
                        // so each list refetches and a slow response can never
                        // paint into the wrong list.
                        <DynamicLookupList lookup={selectedLookup} />
                    ) : (
                        parentElements[tab]
                    )}
                </Box>
            </Container>
        </div>
    );
}
